/**
 * Date Formatting Utilities
 * 
 * Utility functions for formatting dates in a user-friendly way.
 */

/**
 * Formats a date string or Date object into a human-readable relative time string.
 * Examples: "Just now", "2 minutes ago", "3 hours ago", "2 days ago", "Dec 26, 2025"
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string
 * 
 * @example
 * ```tsx
 * formatRelativeDate('2025-12-26T21:18:57.514Z') // "2 days ago"
 * formatRelativeDate(new Date()) // "Just now"
 * ```
 */
export function formatRelativeDate(dateString: string | Date | undefined | null): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  
  // For dates older than a week, use a more specific format
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Formats a date string or Date object into a readable date string.
 * Example: "Dec 26, 2025"
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string
 * 
 * @example
 * ```tsx
 * formatDate('2025-12-26T21:18:57.514Z') // "Dec 26, 2025"
 * ```
 */
export function formatDate(dateString: string | Date | undefined | null): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

